import { GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import { enqueueSnackbar } from 'notistack';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useFetchData } from '../../hooks/useFetchData';
import { Folder, FolderResponse } from '../../Types/folder';
import { projectFoldersPathUrl } from '../../util/routesUrls';
import { useAuth } from './AuthProvider';

interface ProjectsContextType {
  hasProjects: boolean;
  hasArchived: boolean;
  setShouldFetch: React.Dispatch<React.SetStateAction<boolean>>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  sortModel: GridSortModel | undefined;
  setSortModel: React.Dispatch<React.SetStateAction<GridSortModel>>;
  filterModel: GridFilterModel | undefined;
  setFilterModel: React.Dispatch<React.SetStateAction<GridFilterModel>>;
  protectedSetAdminShowAll: (value: boolean) => boolean;
  totalPages: number;
  projectCount: number;
  isLoading: boolean;
  adminShowAll: boolean;
  protectedSetShowArchived: (value: boolean) => boolean;
  showArchived: boolean;
  toggleArchiveChecked: boolean;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  isSearching: boolean;
  setIsSearching: React.Dispatch<React.SetStateAction<boolean>>;
  projectFolders: Folder[];
}

export const ProjectsContext = createContext<ProjectsContextType | null>(null);

export const ProjectsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [projectFolders, setProjectFolders] = useState<Folder[]>([]);
  const [hasProjects, setHasProjects] = useState(true);
  const [hasArchived, setHasArchived] = useState(true);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [adminShowAll, setAdminShowAll] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [toggleArchiveChecked, setToggleArchiveChecked] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(6);
  const [totalPages, setTotalPages] = useState(0);
  const [projectCount, setProjectCount] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const { isDealGPTAdmin } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const urlPath = projectFoldersPathUrl({
    foldersWithProjects: true,
    limit: 15,
    offset: 0,
    dealgptReturnAll: adminShowAll,
    showArchived,
    page,
    pageSize,
    sortField: sortModel[0]?.field,
    sortDirection: sortModel[0]?.sort ?? undefined,
    filterModel: JSON.stringify(filterModel),
    projectSearchQuery: searchQuery,
  });

  const { data: foldersData, loading } = useFetchData<FolderResponse>({
    urlPath,
    skip: !shouldFetch,
  });

  const protectedSetAdminShowAll = useCallback(
    (value: boolean) => {
      if (value && isDealGPTAdmin) setAdminShowAll(true);
      else {
        if (value)
          enqueueSnackbar('You do not have the required permissions to view all projects', {
            variant: 'error',
          });
        setAdminShowAll(false);
      }
      setShouldFetch(true);
      return value && isDealGPTAdmin;
    },
    [isDealGPTAdmin]
  );

  const protectedSetShowArchived = useCallback((value: boolean) => {
    //If the toggle in te UI is clicked means that if the provider returns no archived projects it should not redirect to NoAccess view.
    setToggleArchiveChecked(true);
    setShowArchived(value);
    setShouldFetch(true);
    return value;
  }, []);

  const value = useMemo(
    () => ({
      projectFolders,
      hasProjects,
      hasArchived,
      setShouldFetch,
      page,
      setPage,
      pageSize,
      setPageSize,
      totalPages,
      projectCount,
      sortModel,
      setSortModel,
      filterModel,
      setFilterModel,
      isLoading: loading,
      protectedSetAdminShowAll,
      protectedSetShowArchived,
      adminShowAll,
      showArchived,
      toggleArchiveChecked,
      searchQuery,
      setSearchQuery,
      isSearching,
      setIsSearching,
    }),
    [
      hasProjects,
      hasArchived,
      setShouldFetch,
      page,
      setPage,
      pageSize,
      setPageSize,
      totalPages,
      projectCount,
      sortModel,
      setSortModel,
      filterModel,
      setFilterModel,
      loading,
      protectedSetAdminShowAll,
      protectedSetShowArchived,
      adminShowAll,
      showArchived,
      toggleArchiveChecked,
      searchQuery,
      isSearching,
      projectFolders,
    ]
  );

  useEffect(() => {
    if (foldersData) {
      const allFolders: Folder[] = [...foldersData.pinnedFolders, ...foldersData.unpinnedFolders];

      setProjectFolders(allFolders);
      setHasProjects(allFolders.length > 0);
      setHasArchived(foldersData.totalCount > 0);
      setProjectCount(foldersData.totalCount);
      setTotalPages(Math.ceil(foldersData.totalCount / pageSize));
      setIsSearching(searchQuery !== '');
      setShouldFetch(false);
    }
  }, [foldersData, pageSize, searchQuery]);

  return <ProjectsContext.Provider value={value}>{children}</ProjectsContext.Provider>;
};

export const useProjects = (): ProjectsContextType => {
  const projectsContext = useContext(ProjectsContext);

  if (!projectsContext) {
    throw new Error('useProjects must be used within a ProjectsProvider');
  }

  return projectsContext;
};
